import { Route } from '@angular/router';
import {calendarDataResolver, initialDataResolver} from 'app/app.resolvers';
import { AuthGuard } from './core/guards/auth.guard';
import { NoAuthGuard } from './core/guards/noAuth.guard';
import { LayoutComponent } from './core/components/layout.component';
import {Error404Component} from "./shared/components/error/error-404/error-404.component";
import {AgreementsDocs} from "./shared/components/terms-and-conditions/terms-and-conditions.component";
import {
    AcordPacientInformatTemplateSibV2
} from "./features/documents/acord-pacient-informat/acord-pacient-informat-sib-v2/template/acord-pacient-informat-template-sib-v2.component";
import {FormsWizardsComponent} from "./features/documents/wizzard-wrapper-component/wizards.component";
import {TemplateWizzardComponent} from "./features/documents/template-wrapper-component/template.wrapper.component";
import {cartResolver, invoiceInfoResolver} from "./features/admin/admin.resolver";
import {platformDataResolver} from "./features/platform/platform.routes.resolver";
import {MedicOwnerDash} from "./features/home/components/dash-boards/medic-dash/medic-dash.component";
import {ContactPageComponent} from "./shared/components/public-contact/contact-page";
import {PricesPageComponent} from "./shared/components/public-prices/prices-page";
import {PrintPage} from "./features/print/print-page/print.page";
import {
    AppointmentConfirmationComponent
} from "./shared/components/auth/appointment-confirmation/components/appointment-confirmation.component";

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [
    // Redirect empty path to '/example'
    { path: '', pathMatch: 'full', redirectTo: 'home' },

    // Redirect signed-in user to the '/example'
    //
    // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    { path: 'signed-in-redirect', pathMatch: 'full', redirectTo: 'platform' },

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'smm-contact',
                component: ContactPageComponent,
            },
            {
                path: 'smm-prices',
                component: PricesPageComponent,
            },
            {
                path: 'confirmation-required',
                loadChildren: () =>
                    import(
                        'app/shared/components/auth/confirmation-required/components/confirmation-required.routes'
                    ),
            },
            {
                path: 'forgot-password',
                loadChildren: () =>
                    import(
                        'app/shared/components/auth/forgot-password/components/forgot-password.routes'
                    ),
            },
            {
                path: 'reset-password',
                loadChildren: () =>
                    import(
                        'app/shared/components/auth/reset-password/components/reset-password.routes'
                    ),
            },
            {
                path: 'sign-in',
                loadChildren: () =>
                    import(
                        'app/shared/components/auth/sign-in/components/sign-in.routes'
                    ),
            },
            {
                path: 'sign-up',
                loadChildren: () =>
                    import(
                        'app/shared/components/auth/sign-up/components/sign-up.routes'
                    ),
            },
            {
                path: 'agreements',
                component: AgreementsDocs
            },
            {
                path: 'p',
                component: AppointmentConfirmationComponent
            },
        ],
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'sign-out',
                loadChildren: () =>
                    import(
                        'app/shared/components/auth/sign-out/components/sign-out.routes'
                    ),
            },
            {
                path: 'unlock-session',
                loadChildren: () =>
                    import(
                        'app/shared/components/auth/unlock-session/components/unlock-session.routes'
                    ),
            },
        ],
    },

    // Platform owner routes
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard, ],
        component: LayoutComponent,
        resolve: {
            data: platformDataResolver
        },
        children: [
            {
                path: 'platform',
                loadChildren: () => import('app/features/platform/platform.routes'),
            }
        ],
    },

    {
        path: 'printing',
        loadChildren: () => import('app/features/print/print.routes'),
    },

    // Feature routes
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard, ],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
            cartData: cartResolver,
            invoiceData: invoiceInfoResolver,
        },
        children: [
            {
                path: 'policy',
                component: AgreementsDocs
            },
            {
                path: 'contact',
                component: ContactPageComponent,
            },
            {
                path: 'home',
                loadChildren: () => import('app/features/home/home.routes'),
            },
            {
                path: 'admin',
                loadChildren: () => import('app/features/admin/admin.routes'),
            },
            {
                path: 'financial',
                loadChildren: () => import('app/features/admin/admin.routes'),
            },
            {
                path: 'user',
                loadChildren: () => import('app/features/user/user.routes'),
            },
            {
                path: 'patients',
                loadChildren: () => import('app/features/patient/patient.routes'),
            },
            {
                path: 'calendar',
                loadChildren: () => import('app/features/calendar/calendar.routes'),
                resolve: {
                    initialData: calendarDataResolver,
                },
            },
            {
                path: 'sterilization',
                loadChildren: () => import('app/features/sterilization/sterilization.routes'),
            },
            {
                path: 'learning',
                loadChildren: () => import('app/features/academy/academy.routes'),
            },
            {
                path: 'docs',
                component: TemplateWizzardComponent,
            },
            {
                path: 'wizz',
                component: FormsWizardsComponent,
            },
            {
                path: 'recall',
                loadChildren: () => import('app/features/recall/recall.routes'),
            },
            {
                path: 'printing',
                loadChildren: () => import('app/features/print/print.routes'),
            },
        ],
    },
    {
        path: '**',
        component: Error404Component,
    },
];
